import ApiService from './api.service';
import BackgroundService from './bg.service';

export interface SnowflakeDetails {
  auth_method: 'PASSWORD' | 'KEYPAIR';
  host: string;
  username: string;
  password: string;
  private_key: string;
  private_key_passphrase: string;
  database: string;
  query_warehouse: string;
  loading_warehouse: string;
  build_warehouse: string;
  role: string;
  loader_username?: string;
  loader_password?: string;
  loader_private_key?: string;
  loader_private_key_passphrase?: string;
  loader_database?: string;
  loader_role?: string;
}

export interface TenantRegistration {
  // NOTE: the JSON response uses snake_case keys — this field must stay as
  // `tenant_registration`, not `tenantRegistration`.
  tenant_registration: {
    name?: string;
    embed_frame_ancestors?: string[] | null;
  };
  tenant_config: {
    id: string;
    tenant_slug?: string;
    tenant_name?: string;
    infra: {
      is_ready: boolean;
      snowpipe?: {
        setup_complete: boolean;
        setup_error?: string;
      };
    };
    onboarding_info?: any;
    last_used_template?: string;
    build_schedule?: {
      cron_expression?: string;
      timezone?: string;
      enabled?: boolean;
    };
    format_locale?: FormatLocale;
    custom_domain?: string | null;
    custom_domain_cert?: CustomDomainCert | null;
    dbt_variables?: Record<string, string>;
    ellie_allow_sql_queries?: boolean;
    ellie_custom_instructions?: string;
  };
  connection_creds: {
    auth_method?: string;
    private_key?: string;
    private_key_passphrase?: string;
    host: string;
    username: string;
    password: string;
    database: string;
    role: string;
    query_warehouse: string;
    build_warehouse: string;
  };
  loader_creds: {
    private_key?: string;
    private_key_passphrase?: string;
    loading_warehouse: string;
    role: string;
    username: string;
    password: string;
    database: string;
  };
}

export interface SetupScriptResponse {
  ok: boolean;
  snowflake_setup_sql: string;
}

export interface VerifyAccessResponse {
  has_db_access: boolean;
}

export interface InitInfrastructureResponse {
  job_id: string;
}

export interface InfrastructureResult {
  is_ready: boolean;
  snowpipe?: {
    setup_complete: boolean;
    setup_error?: string;
  };
}

export interface BuildSchedule {
  cron_expression: string;
  timezone: string;
  enabled: boolean;
}

export interface FormatLocale {
  decimal: string;
  thousands: string;
  grouping: number[];
  currency: [string, string];
}

export interface OidcConfig {
  issuer_url: string;
  client_id: string;
  scopes: string;
  authorization_endpoint: string | null;
  token_endpoint: string | null;
  userinfo_endpoint: string | null;
  default_role: string;
  role_mapping: Record<string, string> | null;
  auto_provision: boolean;
  enabled: boolean;
}

export interface SnowflakeSsoUser {
  email: string;
  name: string;
  frontegg_user_id: string;
  snowflake_roles: string[];
  default_role: string;
}

export interface SnowflakeSsoConfig {
  enabled: boolean;
  users: SnowflakeSsoUser[];
  last_synced_at: string | null;
  sync_error: string | null;
  issuer_url: string;
}

export interface SnowflakeSsoLoginResponse {
  saml_response: string;
  acs_url: string;
}

export interface TenantUser {
  id: string;
  email: string;
  name: string;
}

export type CustomDomainCertStatus = 'PENDING_VALIDATION' | 'ISSUED' | 'ATTACHED' | 'FAILED';

export interface CustomDomainCert {
  acm_cert_arn: string | null;
  status: CustomDomainCertStatus;
  validation_cname_name: string | null;
  validation_cname_value: string | null;
  cname_target: string | null;
  requested_at: string | null;
  issued_at: string | null;
  attached_at: string | null;
}

export interface SaveCustomDomainResponse {
  record: any;
  custom_domain_cert: CustomDomainCert | null;
}

export interface VerifyCustomDomainResponse {
  custom_domain_cert: CustomDomainCert;
}

export function isValidSnowflakeURL(url: string): boolean {
  if (url) {
    // The URL should contain the account name followed by ".snowflakecomputing.com"
    const regex = /^([^.]+\.)?([^.]+\.)([^.]+\.)?snowflakecomputing\.com$/;
    const match = url.match(regex);
    if (match !== null) {
      return true;
    }
  }
  return false;
}

export default class TenantConfigService {
  private static instance: TenantConfigService;

  public static getInstance(): TenantConfigService {
    if (!TenantConfigService.instance) {
      TenantConfigService.instance = new TenantConfigService();
    }
    return TenantConfigService.instance;
  }

  constructor() {}

  public async getTenantRegistration(): Promise<TenantRegistration> {
    return (await ApiService.getInstance().request(
      'GET',
      '/tenant-config/init'
    )) as TenantRegistration;
  }

  public async saveSnowflakeDetails(
    tenantConfigId: string,
    details: Partial<SnowflakeDetails>
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/snowflake-creds`,
      details
    );
  }

  public async showSetupScript(
    tenantConfigId: string
  ): Promise<SetupScriptResponse> {
    return (await ApiService.getInstance().request(
      'POST',
      `/tenant-config/${tenantConfigId}/snowflake-setup-script`,
      {}
    )) as SetupScriptResponse;
  }

  public async verifySnowflakeAccess(
    tenantConfigId: string
  ): Promise<VerifyAccessResponse> {
    return (await ApiService.getInstance().request(
      'GET',
      `/tenant-config/${tenantConfigId}/verify-snowflake-creds`
    )) as VerifyAccessResponse;
  }

  public async initInfrastructure(
    tenantConfigId: string
  ): Promise<InfrastructureResult> {
    const response = (await ApiService.getInstance().request(
      'POST',
      `/tenant-config/${tenantConfigId}/init-infrustructure`,
      {}
    )) as InitInfrastructureResponse;

    // Wait for the background job to complete
    const result = await BackgroundService.getInstance().waitForJob(
      response.job_id
    );

    // The job result is the full tenant config object; extract the infra fields
    const infra = result?.infra || result;
    return {
      is_ready: infra.is_ready,
      snowpipe: infra.snowpipe,
    } as InfrastructureResult;
  }

  public async saveBuildSchedule(
    tenantConfigId: string,
    schedule: BuildSchedule
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/build-schedule`,
      schedule
    );
  }

  public async saveVariables(
    tenantConfigId: string,
    variables: Record<string, string>
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/update-variables`,
      variables
    );
  }

  public async saveWhitelabeling(
    tenantConfigId: string,
    whitelabeling: Record<string, string | boolean | null>
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PATCH',
      `/tenant-config/${tenantConfigId}/whitelabeling`,
      whitelabeling
    );
  }

  public async saveWhitelabelingLogo(
    tenantConfigId: string,
    fileId: string
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/whitelabeling/logo/${fileId}`,
      {}
    );
  }

  public async saveWhitelabelingFavicon(
    tenantConfigId: string,
    fileId: string
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/whitelabeling/favicon/${fileId}`,
      {}
    );
  }

  public async saveWhitelabelingFont(
    tenantConfigId: string,
    slot: 'ui' | 'metric',
    fileId: string
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/whitelabeling/font/${slot}/${fileId}`,
      {}
    );
  }

  public async saveFormatLocale(
    tenantConfigId: string,
    formatLocale: FormatLocale
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/format-locale`,
      { format_locale: formatLocale }
    );
  }

  public async saveDefaultDashboard(
    tenantConfigId: string,
    defaultDashboardId: string | null
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/default-dashboard`,
      { default_dashboard_id: defaultDashboardId }
    );
  }

  public async saveDashboardSupersetsEnabled(
    tenantConfigId: string,
    enabled: boolean
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/dashboard-supersets-enabled`,
      { dashboard_supersets_enabled: enabled }
    );
  }

  public async saveDashboardDateRangeOptions(
    tenantConfigId: string,
    options: string[] | null
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/dashboard-date-range-options`,
      { dashboard_date_range_options: options }
    );
  }

  public async saveCustomDomain(
    tenantConfigId: string,
    customDomain: string | null
  ): Promise<SaveCustomDomainResponse> {
    return (await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/custom-domain`,
      { custom_domain: customDomain }
    )) as SaveCustomDomainResponse;
  }

  public async verifyCustomDomain(
    tenantConfigId: string
  ): Promise<VerifyCustomDomainResponse> {
    return (await ApiService.getInstance().request(
      'POST',
      `/tenant-config/${tenantConfigId}/custom-domain/verify`,
      {}
    )) as VerifyCustomDomainResponse;
  }

  public async saveEmbedFrameAncestors(
    tenantConfigId: string,
    origins: string[]
  ): Promise<{ embed_frame_ancestors: string[] }> {
    return (await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/embed-frame-ancestors`,
      { embed_frame_ancestors: origins }
    )) as { embed_frame_ancestors: string[] };
  }

  // ── OIDC SSO ──────────────────────────────────────────────────────

  public async getOidcConfig(
    tenantConfigId: string
  ): Promise<{ record: OidcConfig | null; has_client_secret: boolean }> {
    return (await ApiService.getInstance().request(
      'GET',
      `/tenant-config/${tenantConfigId}/oidc`
    )) as { record: OidcConfig | null; has_client_secret: boolean };
  }

  public async saveOidcConfig(
    tenantConfigId: string,
    config: Partial<OidcConfig> & { client_secret?: string }
  ): Promise<{ record: OidcConfig | null; has_client_secret: boolean }> {
    return (await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/oidc`,
      config
    )) as { record: OidcConfig | null; has_client_secret: boolean };
  }

  public async deleteOidcConfig(
    tenantConfigId: string
  ): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/tenant-config/${tenantConfigId}/oidc`,
      {}
    );
  }

  // ── Snowflake SSO ───────────────────────────────────────────────────

  public async getSnowflakeSsoConfig(
    tenantConfigId: string
  ): Promise<{ record: SnowflakeSsoConfig; is_hosted: boolean; available_roles: string[] }> {
    return (await ApiService.getInstance().request(
      'GET',
      `/tenant-config/${tenantConfigId}/snowflake-sso`
    )) as { record: SnowflakeSsoConfig; is_hosted: boolean; available_roles: string[] };
  }

  public async updateSnowflakeSsoConfig(
    tenantConfigId: string,
    config: Partial<SnowflakeSsoConfig>
  ): Promise<SnowflakeSsoConfig> {
    const resp = (await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/snowflake-sso`,
      config
    )) as { record: SnowflakeSsoConfig };
    return resp.record;
  }

  public async syncSnowflakeSso(tenantConfigId: string): Promise<void> {
    const response = (await ApiService.getInstance().request(
      'POST',
      `/tenant-config/${tenantConfigId}/snowflake-sso/sync`,
      {}
    )) as { job_id: string };

    await BackgroundService.getInstance().waitForJob(response.job_id);
  }

  public async generateSsoScript(
    tenantConfigId: string
  ): Promise<string> {
    const resp = (await ApiService.getInstance().request(
      'POST',
      `/tenant-config/${tenantConfigId}/snowflake-sso/script`,
      {}
    )) as { ok: boolean; script: string };
    return resp.script;
  }

  public async getSnowflakeSsoStatus(
    tenantConfigId: string
  ): Promise<{ can_login: boolean }> {
    return (await ApiService.getInstance().request(
      'GET',
      `/tenant-config/${tenantConfigId}/snowflake-sso/status`
    )) as { can_login: boolean };
  }

  public async snowflakeSsoLogin(
    tenantConfigId: string
  ): Promise<SnowflakeSsoLoginResponse> {
    return (await ApiService.getInstance().request(
      'POST',
      `/tenant-config/${tenantConfigId}/snowflake-sso/login`,
      {}
    )) as SnowflakeSsoLoginResponse;
  }

  public async saveEllieSettings(
    tenantConfigId: string,
    settings: { ellie_allow_sql_queries?: boolean; ellie_custom_instructions?: string }
  ): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/${tenantConfigId}/ellie-settings`,
      settings
    );
  }

  public async getTenantUsers(): Promise<TenantUser[]> {
    const resp = (await ApiService.getInstance().request(
      'GET',
      '/tenant-config/users'
    )) as { records: TenantUser[] };
    return resp.records;
  }
}
